import { Alert, Dialog, DialogContent } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { ReactElement } from 'react';

import { getMultiplexPanels, getMultiplexPanelsQueryKey } from 'api/multiplexPanels';
import { DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import Loader from 'components/Loader';
import { useCasesParams } from 'utils/useCasesParams';
import { CasesParams } from 'utils/useCasesParams/types';
import { MultiplexSelectionAlert } from '../MultiplexSelectionAlert';
import { useMultiplexActionSelection } from '../useMultiplexActionSelection';
import { PanelSpotDetectionForm } from './PanelSpotDetectionForm';

export interface RunMultiplexPeakDetectionProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams?: CasesParams;
  platformVersionWarning?: React.ReactNode;
}

export const RunMultiplexPeakDetection = ({
  onClose,
  casesParams: casesParamsOverride,
  platformVersionWarning,
}: RunMultiplexPeakDetectionProps): ReactElement => {
  const { casesParams: urlCasesParams } = useCasesParams();
  const selection = useMultiplexActionSelection(casesParamsOverride || urlCasesParams);
  const scope = selection.effectiveCasesParams || casesParamsOverride || urlCasesParams;
  const studyId = scope.studyId || (typeof scope.filters?.studyId === 'string' ? scope.filters.studyId : undefined);
  const panelsQuery = useQuery({
    queryKey: getMultiplexPanelsQueryKey(scope),
    queryFn: ({ signal }) => getMultiplexPanels(scope, signal),
    enabled: Boolean(selection.effectiveCasesParams && studyId),
  });
  const renderDialog = (content: React.ReactNode) => (
    <Dialog open maxWidth="xl" fullWidth onClose={() => onClose()} aria-labelledby="spot-detection-title">
      <DialogChromeHeader title="Run Spot Detection" titleId="spot-detection-title" onClose={() => onClose()} />
      {content}
    </Dialog>
  );
  if (selection.isLoadingSelection) {
    return renderDialog(
      <DialogContent dividers>
        <MultiplexSelectionAlert
          severity="info"
          title="Checking slide selection"
          message="Resolving the multiplex slides for this action."
        />
      </DialogContent>
    );
  }
  if (selection.blockingMessage || !studyId) {
    return renderDialog(
      <DialogContent dividers>
        <MultiplexSelectionAlert
          severity="error"
          title="Select slides for this multiplex action"
          message={selection.blockingMessage || 'Select a study before spot detection.'}
        />
      </DialogContent>
    );
  }
  if (panelsQuery.isError) {
    return renderDialog(
      <DialogContent dividers>
        <Alert severity="error">Panel settings could not be loaded. Close and reopen spot detection to retry.</Alert>
      </DialogContent>
    );
  }
  if (panelsQuery.isLoading || !panelsQuery.data) {
    return renderDialog(
      <DialogContent dividers>
        <Loader inline message="Loading panel settings" />
      </DialogContent>
    );
  }
  const slideIds = selection.selectedMultiplexSlideIds;
  return renderDialog(
    <PanelSpotDetectionForm
      key={JSON.stringify([studyId, [...slideIds].sort()])}
      response={panelsQuery.data}
      casesParams={scope}
      selectedSlideIds={slideIds}
      onClose={onClose}
      scopeMessage={selection.infoMessage}
      platformVersionWarning={platformVersionWarning}
    />
  );
};
